import { SpotlightItem, SpotlightItemType } from '@thoughtspot/global-nav';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { useCallback, useMemo, useState } from 'react';
import {
    GetPinboardsListQuery,
    useGetPinboardsListQuery as _useGetPinboardsListQuery,
} from '../../services/generated/graphql-types';

type LiveboardObject = NonNullable<
    NonNullable<GetPinboardsListQuery['getPinboardList']['objects']>[number]
>;

/**
 * Lazily fetches liveboards visible to the user and converts them to
 * SpotlightItem[]. The fetch is triggered by calling activate().
 *
 * Used when the user selects the "Liveboard" filter in the command palette.
 */
export function useSpotlightLiveboardItems() {
    const [isActive, setIsActive] = useState(false);

    const { data, loading, refetch } = _useGetPinboardsListQuery({
        fetchPolicy: 'network-only',
        skip: !isActive,
        variables: {
            params: {
                autoCreated: false,
                showHidden: false,
                sortAscending: false,
                fetchPermissions: false,
                batchSize: 100,
            },
        },
    });

    const liveboardItems = useMemo<SpotlightItem[]>(() => {
        const objects = data?.getPinboardList?.objects;
        if (!objects) return [];
        return objects.map((lb: LiveboardObject) => {
            const authorLabel = lb.authorDisplayName || lb.authorName;
            return {
                id: `lb-spotlight-${lb.id}`,
                label: lb.name,
                description: authorLabel ? `by ${authorLabel}` : undefined,
                icon: IconID.PINBOARD_NEW,
                type: SpotlightItemType.NAVIGATION,
                path: `/pinboard/${lb.id}`,
                keywords: ['liveboards', 'dashboard'],
            };
        });
    }, [data]);

    const activate = useCallback(() => {
        if (isActive) {
            refetch();
        } else {
            setIsActive(true);
        }
    }, [isActive, refetch]);

    return { liveboardItems, liveboardItemsLoading: loading, activate };
}
